/**
 * Shared schemas for the "Request a Feature" surface.
 *
 * Both the Express server and the React frontend import these via
 * `@shared/schemas/featureRequest`. A rename on one side would silently
 * break the other (the request body would just fail Zod validation with a
 * confusing 400), so per CLAUDE.md's shared-schema rule these enums + Zod
 * shapes are the single source of truth.
 *
 * Mirrors the convention in `api.ts` — request/response Zod object, then a
 * z.infer type alias, then an extra const for fixed UI values that both
 * sides need (here: the credit-reward amount and the status list).
 */

import { z } from "zod";

// ── Enums ─────────────────────────────────────────────────────────────────

/** Where in the UI the user opened the dialog from. Drives placement A/B. */
export const featureRequestSourceSchema = z.enum([
  "footer",
  "profile",
  "post_generation",
  "other",
]);
export type FeatureRequestSource = z.infer<typeof featureRequestSourceSchema>;

/** Coarse category. Picks signal-to-noise > free-text tagging. */
export const featureRequestCategorySchema = z.enum([
  "new_flow",
  "improve_flow",
  "pricing",
  "account",
  "other",
]);
export type FeatureRequestCategory = z.infer<typeof featureRequestCategorySchema>;

/** Admin-managed lifecycle state of a request. */
export const featureRequestStatusSchema = z.enum([
  "new",
  "under_review",
  "planned",
  "in_progress",
  "shipped",
  "declined",
]);
export type FeatureRequestStatus = z.infer<typeof featureRequestStatusSchema>;

/** Ordered list for the admin filter dropdown (kept here for UI consistency). */
export const FEATURE_REQUEST_STATUSES: readonly FeatureRequestStatus[] = [
  "new",
  "under_review",
  "planned",
  "in_progress",
  "shipped",
  "declined",
] as const;

// ── Field limits (mirrored in the SQL CHECK constraints) ──────────────────

export const FEATURE_REQUEST_TITLE_MIN = 3;
export const FEATURE_REQUEST_TITLE_MAX = 80;
export const FEATURE_REQUEST_DESCRIPTION_MIN = 10;
export const FEATURE_REQUEST_DESCRIPTION_MAX = 1000;
/** Optional contact phone — generous cap covers international + formatting. */
export const FEATURE_REQUEST_PHONE_MAX = 40;

/**
 * One-time credit reward granted to a user on their first submission. The
 * frontend imports this so the dialog can preview "+{N} credits" copy
 * without hardcoding the value.
 */
export const FEATURE_REQUEST_REWARD_CREDITS = 10;

/**
 * Credit-transactions operation tag for the one-time grant. Stored in the
 * `credit_transactions.operation` column so the bonus is visible in the
 * existing credit-history audit trail.
 */
export const FEATURE_REQUEST_CREDIT_OPERATION = "feature_request_reward" as const;

// ── API: POST /api/feature-requests ───────────────────────────────────────

export const submitFeatureRequestRequestSchema = z.object({
  title: z
    .string()
    .min(FEATURE_REQUEST_TITLE_MIN)
    .max(FEATURE_REQUEST_TITLE_MAX),
  description: z
    .string()
    .min(FEATURE_REQUEST_DESCRIPTION_MIN)
    .max(FEATURE_REQUEST_DESCRIPTION_MAX),
  category: featureRequestCategorySchema,
  source: featureRequestSourceSchema,
  notifyOnShip: z.boolean(),
  /** Optional — submitter's phone so the team can follow up if they want to. */
  phone: z.string().max(FEATURE_REQUEST_PHONE_MAX).optional(),
});
export type SubmitFeatureRequestRequest = z.infer<
  typeof submitFeatureRequestRequestSchema
>;

export const submitFeatureRequestResponseSchema = z.object({
  id: z.string().uuid(),
  /** 0 if the user already received the one-time reward on a prior submission. */
  creditsGranted: z.number().int().nonnegative(),
});
export type SubmitFeatureRequestResponse = z.infer<
  typeof submitFeatureRequestResponseSchema
>;

// ── API: GET /api/feature-requests/mine ───────────────────────────────────

export const featureRequestRowSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  category: featureRequestCategorySchema,
  status: featureRequestStatusSchema,
  adminNotes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FeatureRequestRow = z.infer<typeof featureRequestRowSchema>;

export const listMyFeatureRequestsResponseSchema = z.object({
  requests: z.array(featureRequestRowSchema),
});
export type ListMyFeatureRequestsResponse = z.infer<
  typeof listMyFeatureRequestsResponseSchema
>;

// ── API: admin endpoints ──────────────────────────────────────────────────

export const adminFeatureRequestRowSchema = featureRequestRowSchema.extend({
  userId: z.string().uuid(),
  userEmail: z.string().nullable(),
  source: featureRequestSourceSchema,
  notifyOnShip: z.boolean(),
  phone: z.string().nullable(),
});
export type AdminFeatureRequestRow = z.infer<typeof adminFeatureRequestRowSchema>;

export const listAdminFeatureRequestsResponseSchema = z.object({
  requests: z.array(adminFeatureRequestRowSchema),
});
export type ListAdminFeatureRequestsResponse = z.infer<
  typeof listAdminFeatureRequestsResponseSchema
>;

export const updateAdminFeatureRequestRequestSchema = z.object({
  status: featureRequestStatusSchema.optional(),
  adminNotes: z.string().max(2000).nullable().optional(),
});
export type UpdateAdminFeatureRequestRequest = z.infer<
  typeof updateAdminFeatureRequestRequestSchema
>;
